// S12 · Step 5 — Quality checks (`/onboarding/quality`, Module 02 §3.5b/§4 S12,
// D-214).
//
// The owner's requirement verbatim: the factory WRITES the thickness it checks
// for, with an accepted range, during setup. The pack installed control points
// with pack-default bands; this screen shows them grouped by stage and makes
// the measurement bands editable in place.
//
// The load-bearing asymmetry (§3.5b pts 2–3):
//   * EDITING a band goes through the ONLY redefinition door,
//     `POST /v1/quality/control-points/:id/versions`, which mints version N+1
//     and — for a pack point — demotes `source` to `custom`. That demotion is
//     the FEATURE (the band stops being the pack's), and the one-sentence
//     ownership dialog says so before the first save.
//   * CONFIRMING the step is `POST /v1/onboarding/steps/5/complete` and makes
//     NO quality write: confirming through /versions would demote every
//     reviewed point and silently opt the tenant out of every future pack
//     correction (D-205 convergence).
//   * DISABLE rides the existing one-way `/deactivate`; `active` is the one
//     column a re-seed never converges, so the choice survives every
//     re-install — the dialog states both facts.
//
// Permissions: the wizard runs on `onboarding.manage_wizard`; band edit and
// deactivate need `quality.catalog.manage`. The degrade is decided OFF THE
// API'S OWN 403 (the S5 people-panel pattern — never a guessed grant table): a
// refused write flips the screen read-only with the notice, and review +
// confirm keep working.

import { useMemo, useState } from "react";
import { formatQuantity } from "../../../lib/quantity";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { components } from "@assessly/api-client";
import { Field } from "../../../components/field";
import { EmptyState, ErrorState, TableSkeleton } from "../../../components/states";
import { client } from "../../../lib/api";
import { RequestError, newIdempotencyKey, unwrap } from "../../../lib/request";
import { SaveContinueBar } from "../components/save-continue-bar";
import { WizardDialog } from "../components/wizard-dialog";
import { useOnboardingSession } from "../hooks/onboarding-queries";
import { useQualityPermissions } from "../../office/quality/hooks/permissions";
import { isWizardFrozen } from "../lib/wizard-steps";
import "./quality/quality-review.css";

type S = components["schemas"];
type ControlPoint = S["QualityControlPoint"];

const STEP_NO = 5;
const STAGES = ["receiving", "in_process", "final"] as const;
type Stage = (typeof STAGES)[number];

/** Rev-7: at most this many points show open at once; above it the stages
 * fold and only the first stays open (on staging the step held 2,903 points
 * and rendered 299,617 px tall). */
export const OPEN_ALL_LIMIT = 20;
/** Points per page inside a stage. */
export const STAGE_PAGE_SIZE = 20;

/** The band's three numbers, trimmed to what was recorded (1.950000 → 1.95),
 * with the unit written once at the end. */
function bandNumber(value: string | null, locale: string): string {
  return value === null ? "—" : formatQuantity(value, { precision: 6, locale });
}

const pointsKey = ["onboarding", "quality-review", "control-points"] as const;

function useControlPoints() {
  return useQuery({
    queryKey: pointsKey,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/quality/control-points", {
          params: { query: { is_current: "true" } },
          signal,
        }),
        "quality-review-points",
      ),
  });
}

/** The full §2.1 definition of `point`, as the /versions door wants it — with
 * `band` swapped onto the check item at `seq`. Sending the WHOLE current
 * definition is what makes a band edit a redefinition and nothing else. */
function versionBodyWithBand(
  point: ControlPoint,
  seq: number,
  band: { min: string | null; target: string | null; max: string | null },
): S["QualityControlPointCreateRequest"] {
  const orOmit = <T,>(v: T | null): T | undefined => v ?? undefined;
  // exactOptionalPropertyTypes: an optional input key must be ABSENT, never
  // `undefined` — strip the omitted ones after mapping.
  const compact = <T extends object>(o: T): { [K in keyof T]: Exclude<T[K], undefined> } =>
    Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as {
      [K in keyof T]: Exclude<T[K], undefined>;
    };
  return compact({
    code: point.code,
    name: point.name,
    facility_id: orOmit(point.facility_id),
    name_i18n: point.name_i18n,
    stage: point.stage,
    trigger_mode: point.trigger_mode,
    sample_mode: point.sample_mode,
    sample_size: orOmit(point.sample_size),
    sample_pct: orOmit(point.sample_pct),
    aql_inspection_level: orOmit(point.aql_inspection_level),
    aql_value: orOmit(point.aql_value),
    is_blocking: point.is_blocking,
    auto_hold_on_fail: point.auto_hold_on_fail,
    allow_guided_cut: point.allow_guided_cut,
    cut_trim_allowance_m: point.cut_trim_allowance_m,
    min_sellable_length_m: orOmit(point.min_sellable_length_m),
    instructions_i18n: point.instructions_i18n,
    bindings: point.bindings.map((b) =>
      compact({
        material_id: orOmit(b.material_id),
        supplier_id: orOmit(b.supplier_id),
        product_id: orOmit(b.product_id),
      }),
    ),
    check_items: point.check_items.map((i) => {
      const edited = i.seq === seq;
      return compact({
        seq: i.seq,
        name: i.name,
        name_i18n: i.name_i18n,
        check_type: i.check_type,
        uom: orOmit(i.uom),
        min_value: orOmit(edited ? band.min : i.min_value),
        target_value: orOmit(edited ? band.target : i.target_value),
        max_value: orOmit(edited ? band.max : i.max_value),
        checklist_items_i18n: orOmit(i.checklist_items_i18n),
        photo_required: i.photo_required,
        min_photos: i.min_photos,
        is_required: i.is_required,
        appears_on_coa: i.appears_on_coa,
      });
    }),
  });
}

interface BandDraft {
  pointId: string;
  seq: number;
  min: string;
  target: string;
  max: string;
}

/** D4: the server's own refusal, said to the person (problem detail first,
 * then title/code — never a silent close). */
function refusalText(err: unknown): string | null {
  if (!(err instanceof RequestError)) return err instanceof Error ? err.message : null;
  return err.problem?.detail ?? err.problem?.title ?? err.code ?? err.message;
}

function numeric(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

function bandValid(d: BandDraft): boolean {
  const nums = [d.min, d.target, d.max].map((v) => v.trim()).filter((v) => v !== "");
  if (nums.some((v) => Number.isNaN(Number(v)))) return false;
  const min = d.min.trim() === "" ? null : Number(d.min);
  const max = d.max.trim() === "" ? null : Number(d.max);
  return min === null || max === null || min <= max;
}

export function QualityReviewScreen() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const points = useControlPoints();
  const session = useOnboardingSession();
  // §3.5b pt 5 — module 07's OWN permission mirror decides what is offered
  // (the office quality screens' hook — the same grants table those screens
  // trust), and the API's own 403 stays as the backstop for a grant revoked
  // mid-session. The onboarding mirror deliberately knows no quality.* codes.
  const perms = useQualityPermissions();
  // §3.1: a live session freezes the whole wizard — S12 included (B3).
  const frozen = session.data !== undefined && isWizardFrozen(session.data.session);
  const canManageCatalog = perms.has("quality.catalog.manage");
  const [deniedByApi, setDeniedByApi] = useState(false);
  const canEdit = canManageCatalog && !deniedByApi && !frozen;
  const [draft, setDraft] = useState<BandDraft | null>(null);
  const [confirming, setConfirming] = useState<BandDraft | null>(null);
  const [deactivating, setDeactivating] = useState<ControlPoint | null>(null);

  const locale = i18n.language;
  const nameOf = (p: { name_i18n: Record<string, string>; name: string }): string =>
    p.name_i18n[locale] ?? p.name_i18n["en"] ?? p.name;

  const onWriteError = (err: unknown) => {
    if (err instanceof RequestError && err.httpStatus === 403) setDeniedByApi(true);
  };

  const version = useMutation({
    mutationFn: async (input: { point: ControlPoint; d: BandDraft }) =>
      unwrap(
        await client.POST("/v1/quality/control-points/{id}/versions", {
          params: {
            path: { id: input.point.id },
            header: { "Idempotency-Key": newIdempotencyKey() },
          },
          body: versionBodyWithBand(input.point, input.d.seq, {
            min: numeric(input.d.min),
            target: numeric(input.d.target),
            max: numeric(input.d.max),
          }),
        }),
        "quality-review-version",
      ),
    onSuccess: () => {
      setConfirming(null);
      setDraft(null);
      void qc.invalidateQueries({ queryKey: pointsKey });
    },
    onError: (err) => {
      // D4: a refusal RENDERS — the dialog stays open with the problem detail
      // (a silent close reads as success). 403 additionally flips the degrade.
      onWriteError(err);
    },
  });

  const deactivate = useMutation({
    mutationFn: async (point: ControlPoint) =>
      unwrap(
        await client.POST("/v1/quality/control-points/{id}/deactivate", {
          params: {
            path: { id: point.id },
            header: { "Idempotency-Key": newIdempotencyKey() },
          },
        }),
        "quality-review-deactivate",
      ),
    onSuccess: () => {
      setDeactivating(null);
      void qc.invalidateQueries({ queryKey: pointsKey });
    },
    onError: (err) => {
      onWriteError(err);
    },
  });

  const byStage = useMemo(() => {
    const items = points.data?.items ?? [];
    return STAGES.map((stage) => ({
      stage,
      points: items.filter((p) => p.stage === stage),
    })).filter((g) => g.points.length > 0);
  }, [points.data]);

  // Rev-7: which stages are open, and each stage's page. Few points open
  // every stage; many fold all but the first that holds any.
  const manyPoints = (points.data?.items.length ?? 0) > OPEN_ALL_LIMIT;
  const [openOverride, setOpenStages] = useState<ReadonlySet<Stage> | null>(null);
  const openStages: ReadonlySet<Stage> =
    openOverride ??
    new Set(manyPoints ? byStage.slice(0, 1).map((g) => g.stage) : byStage.map((g) => g.stage));
  const [stagePage, setStagePage] = useState<ReadonlyMap<Stage, number>>(new Map());

  const samplingLabel = (p: ControlPoint): string => {
    switch (p.sample_mode) {
      case "fixed_count":
        return t("onboarding.qualityReview.sampling.fixed_count", { n: p.sample_size });
      case "percentage":
        return t("onboarding.qualityReview.sampling.percentage", { pct: p.sample_pct });
      case "first_n":
        return t("onboarding.qualityReview.sampling.first_n", { n: p.sample_size });
      case "aql":
        return t("onboarding.qualityReview.sampling.aql", {
          level: p.aql_inspection_level,
          value: p.aql_value,
        });
      default:
        return t("onboarding.qualityReview.sampling.every_unit");
    }
  };

  const body = () => {
    if (points.isPending)
      return <TableSkeleton rows={6} label={t("onboarding.qualityReview.heading")} />;
    if (points.isError) {
      // B4 / §4 S12: a role without `quality.catalog.read` gets the proper
      // access-denied state (the S1 precedent) — never a retry card inviting
      // them to retry something that will never succeed.
      if (points.error instanceof RequestError && points.error.httpStatus === 403) {
        return (
          <div role="status" data-testid="quality-review-forbidden">
            <EmptyState
              title={t("onboarding.qualityReview.forbiddenTitle")}
              body={t("onboarding.qualityReview.forbiddenBody")}
            />
          </div>
        );
      }
      return (
        <ErrorState
          title={t("onboarding.qualityReview.loadError")}
          retryLabel={t("onboarding.qualityReview.heading")}
          onRetry={() => void points.refetch()}
        />
      );
    }
    if (points.data.items.length === 0) {
      return (
        <EmptyState
          title={t("onboarding.qualityReview.empty.title")}
          body={t("onboarding.qualityReview.empty.body")}
        />
      );
    }
    return (
      <div className="quality-review-groups">
        {byStage.map((group) => {
          const isOpen = openStages.has(group.stage);
          const pageCount = Math.max(1, Math.ceil(group.points.length / STAGE_PAGE_SIZE));
          const page = Math.min(stagePage.get(group.stage) ?? 1, pageCount);
          const start = (page - 1) * STAGE_PAGE_SIZE;
          const shown = group.points.slice(start, start + STAGE_PAGE_SIZE);
          return (
            <section
              key={group.stage}
              className="quality-review-stage"
              data-testid={`quality-review-stage-${group.stage}`}
              data-open={isOpen ? "true" : "false"}
            >
              <h3>
                <button
                  type="button"
                  className="quality-review-stage-toggle"
                  aria-expanded={isOpen}
                  data-testid={`quality-review-stage-toggle-${group.stage}`}
                  onClick={() => {
                    const next = new Set(openStages);
                    if (isOpen) next.delete(group.stage);
                    else next.add(group.stage);
                    setOpenStages(next);
                  }}
                >
                  {t(`onboarding.qualityReview.stage.${group.stage}`)}
                  <span className="quality-review-stage-count num">
                    {t("onboarding.qualityReview.stageCount", { count: group.points.length })}
                  </span>
                </button>
              </h3>
              {isOpen ? (
                <>
                  <ul className="quality-review-points">
                    {shown.map((p) => (
                      <li
                        key={p.id}
                        className={`quality-review-point${p.active ? "" : " is-inactive"}`}
                        data-testid={`quality-review-point-${p.code}`}
                      >
                        <div className="quality-review-point-head">
                          <span className="quality-review-point-name">{nameOf(p)}</span>
                          <span
                            className="quality-review-provenance"
                            data-testid={`quality-review-source-${p.code}`}
                          >
                            {p.source === "vertical_pack"
                              ? t("onboarding.qualityReview.provenance.pack", {
                                  version: p.version,
                                })
                              : t("onboarding.qualityReview.provenance.custom")}
                          </span>
                          <span className="quality-review-sampling">{samplingLabel(p)}</span>
                          {p.is_blocking && (
                            <span className="quality-review-badge">
                              {t("onboarding.qualityReview.badges.blocking")}
                            </span>
                          )}
                          {p.auto_hold_on_fail && (
                            <span className="quality-review-badge">
                              {t("onboarding.qualityReview.badges.autoHold")}
                            </span>
                          )}
                          {!p.active && (
                            <span className="quality-review-badge is-inactive">
                              {t("onboarding.qualityReview.badges.inactive")}
                            </span>
                          )}
                          {canEdit && p.active && (
                            <button
                              type="button"
                              className="ds-btn ds-btn-quiet"
                              data-testid={`quality-review-deactivate-${p.code}`}
                              onClick={() => {
                                setDeactivating(p);
                              }}
                            >
                              {t("onboarding.qualityReview.deactivate.action")}
                            </button>
                          )}
                        </div>
                        <ul className="quality-review-items">
                          {p.check_items.map((item) => {
                            const isMeasurement = item.check_type === "measurement";
                            const editingDraft =
                              draft !== null && draft.pointId === p.id && draft.seq === item.seq
                                ? draft
                                : null;
                            const editing = editingDraft !== null;
                            return (
                              <li
                                key={item.seq}
                                data-testid={`quality-review-item-${p.code}-${item.seq}`}
                              >
                                <span className="quality-review-item-name">{nameOf(item)}</span>
                                {isMeasurement && !editing && (
                                  <span className="quality-review-band">
                                    {t("onboarding.qualityReview.band.min")}{" "}
                                    {bandNumber(item.min_value, locale)} ·{" "}
                                    {t("onboarding.qualityReview.band.target")}{" "}
                                    {bandNumber(item.target_value, locale)} ·{" "}
                                    {t("onboarding.qualityReview.band.max")}{" "}
                                    {bandNumber(item.max_value, locale)}
                                    {item.uom !== null && `\u00a0${item.uom}`}
                                    {canEdit && p.active && (
                                      <button
                                        type="button"
                                        className="ds-btn ds-btn-quiet"
                                        data-testid={`quality-review-edit-${p.code}-${item.seq}`}
                                        onClick={() => {
                                          setDraft({
                                            pointId: p.id,
                                            seq: item.seq,
                                            min: item.min_value ?? "",
                                            target: item.target_value ?? "",
                                            max: item.max_value ?? "",
                                          });
                                        }}
                                      >
                                        {t("onboarding.qualityReview.band.edit")}
                                      </button>
                                    )}
                                  </span>
                                )}
                                {isMeasurement && editingDraft !== null && (
                                  <span className="quality-review-band-editor">
                                    {(["min", "target", "max"] as const).map((field) => (
                                      <Field
                                        key={field}
                                        label={t(`onboarding.qualityReview.band.${field}`)}
                                      >
                                        <input
                                          className="ds-input"
                                          type="text"
                                          inputMode="decimal"
                                          value={editingDraft[field]}
                                          data-testid={`quality-review-band-${field}`}
                                          onChange={(e) => {
                                            setDraft({ ...editingDraft, [field]: e.target.value });
                                          }}
                                        />
                                      </Field>
                                    ))}
                                    {!bandValid(editingDraft) && (
                                      <span className="quality-review-band-invalid" role="alert">
                                        {t("onboarding.qualityReview.band.invalid")}
                                      </span>
                                    )}
                                    <button
                                      type="button"
                                      className="ds-btn ds-btn-primary"
                                      disabled={!bandValid(editingDraft) || version.isPending}
                                      data-testid={`quality-review-save-${p.code}-${item.seq}`}
                                      onClick={() => {
                                        setConfirming(editingDraft);
                                      }}
                                    >
                                      {t("onboarding.qualityReview.band.save")}
                                    </button>
                                    <button
                                      type="button"
                                      className="ds-btn ds-btn-quiet"
                                      onClick={() => {
                                        setDraft(null);
                                      }}
                                    >
                                      {t("onboarding.qualityReview.band.cancel")}
                                    </button>
                                  </span>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </li>
                    ))}
                  </ul>
                  {pageCount > 1 ? (
                    <nav
                      className="quality-review-pager"
                      aria-label={t("onboarding.qualityReview.pager", {
                        stage: t(`onboarding.qualityReview.stage.${group.stage}`),
                      })}
                      data-testid={`quality-review-pager-${group.stage}`}
                    >
                      <span className="num">
                        {t("onboarding.qualityReview.pageRange", {
                          from: start + 1,
                          to: Math.min(start + STAGE_PAGE_SIZE, group.points.length),
                          total: group.points.length,
                        })}
                      </span>
                      <button
                        type="button"
                        className="ds-btn ds-btn-sm"
                        disabled={page <= 1}
                        onClick={() => {
                          setStagePage(new Map(stagePage).set(group.stage, page - 1));
                        }}
                      >
                        {t("onboarding.qualityReview.prev")}
                      </button>
                      <button
                        type="button"
                        className="ds-btn ds-btn-sm"
                        disabled={page >= pageCount}
                        onClick={() => {
                          setStagePage(new Map(stagePage).set(group.stage, page + 1));
                        }}
                      >
                        {t("onboarding.qualityReview.next")}
                      </button>
                    </nav>
                  ) : null}
                </>
              ) : null}
            </section>
          );
        })}
      </div>
    );
  };

  const confirmingPoint =
    confirming === null
      ? null
      : (points.data?.items.find((p) => p.id === confirming.pointId) ?? null);

  return (
    <div className="wizard-step quality-review" data-testid="quality-review-screen">
      <h2>{t("onboarding.qualityReview.heading")}</h2>
      <p className="quality-review-intro">{t("onboarding.qualityReview.intro")}</p>
      {!frozen && !canEdit && (
        <p className="quality-review-readonly" role="status" data-testid="quality-review-readonly">
          {t("onboarding.qualityReview.readOnly")}
        </p>
      )}
      {body()}
      {confirming !== null && confirmingPoint !== null && (
        <WizardDialog
          title={t("onboarding.qualityReview.ownership.title")}
          confirmLabel={t("onboarding.qualityReview.ownership.confirm")}
          onConfirm={() => {
            version.mutate({ point: confirmingPoint, d: confirming });
          }}
          onCancel={() => {
            setConfirming(null);
            version.reset();
          }}
          busy={version.isPending}
          testId="quality-review-ownership-dialog"
        >
          <p>{t("onboarding.qualityReview.ownership.body")}</p>
          {version.error !== null && (
            <p
              className="ds-notice is-error"
              role="alert"
              data-testid="quality-review-version-error"
            >
              {refusalText(version.error)}
            </p>
          )}
        </WizardDialog>
      )}
      {deactivating !== null && (
        <WizardDialog
          title={t("onboarding.qualityReview.deactivate.title")}
          confirmLabel={t("onboarding.qualityReview.deactivate.confirm")}
          onConfirm={() => {
            deactivate.mutate(deactivating);
          }}
          onCancel={() => {
            setDeactivating(null);
            deactivate.reset();
          }}
          destructive
          busy={deactivate.isPending}
          testId="quality-review-deactivate-dialog"
        >
          <p>{t("onboarding.qualityReview.deactivate.body")}</p>
          {deactivate.error !== null && (
            <p
              className="ds-notice is-error"
              role="alert"
              data-testid="quality-review-deactivate-error"
            >
              {refusalText(deactivate.error)}
            </p>
          )}
        </WizardDialog>
      )}
      {/* §3.5b pt 6: the step's bar is CONFIRM-ONLY — no payload write exists
          on this step. Band edits commit through module 07's own doors the
          moment they are confirmed; completing is the ONLY step write, and it
          performs no quality write at all (pt 3). */}
      <SaveContinueBar
        stepNo={STEP_NO}
        getPayload={() => ({})}
        loadedPayload={{}}
        continueTo="/onboarding/checklist"
        frozen={frozen}
        completeOnly
      />
    </div>
  );
}
